import React, { useContext, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Add, Close, FolderOutlined, FolderZipOutlined, PictureAsPdfOutlined } from '@mui/icons-material';
import LocalizationContext from '../../../../localization/LocalizationContext';
import { availableFacets, facetLabel, sampleFolders } from './folderLayout';

const TreeRow = ({ depth, icon, label, caption, muted, onRemove }) => (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pl: depth * 2.5, minHeight: 32, minWidth: 0 }}>
        <Box sx={{ display: 'flex', color: muted ? 'text.disabled' : 'text.secondary', '& svg': { fontSize: 18 } }}>{icon}</Box>
        <Typography variant="body2" noWrap title={label} sx={{ color: muted ? 'text.secondary' : 'text.primary', flex: 1 }}>
            {label}
        </Typography>
        {caption && (
            <Typography variant="caption" color="text.secondary" noWrap>
                {caption}
            </Typography>
        )}
        {onRemove && (
            <IconButton size="small" onClick={onRemove} aria-label={`Remove ${caption} folder`} sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 16 }} />
            </IconButton>
        )}
    </Stack>
);

const DeliveryReportsFolderDialog = ({ open, count, layout, sampleRow, onLayoutChange, onCancel, onDownload }) => {
    const localization = useContext(LocalizationContext);
    const [anchor, setAnchor] = useState(null);
    const addButton = useRef(null);
    const remove = (facet) => {
        onLayoutChange(layout.filter((item) => item !== facet));
        window.requestAnimationFrame(() => addButton.current?.focus());
    };
    const sample = sampleFolders(sampleRow, localization);
    const remaining = availableFacets().filter((facet) => !layout.includes(facet));
    const add = (facet) => {
        setAnchor(null);
        onLayoutChange([...layout, facet]);
    };

    return (
        <Dialog open={open} onClose={onCancel} aria-labelledby="delivery-reports-folder-title" maxWidth="xs" fullWidth>
            <DialogTitle id="delivery-reports-folder-title">
                Download {count} {count === 1 ? 'report' : 'reports'}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {layout.length === 0 ? 'All files go straight into the ZIP. Add folder levels to group them.' : 'Files are grouped into these folders, outermost first.'}
                </Typography>
                <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: 'action.hover' }} data-testid="folder-example">
                    <TreeRow depth={0} icon={<FolderZipOutlined />} label="ZIP file" muted />
                    {layout.map((facet, index) => (
                        <TreeRow key={facet} depth={index + 1} icon={<FolderOutlined />} label={sample[facet]} caption={facetLabel(facet, localization)} onRemove={() => remove(facet)} />
                    ))}
                    {remaining.length > 0 && (
                        <Box sx={{ pl: (layout.length + 1) * 2.5, minHeight: 32, display: 'flex', alignItems: 'center' }}>
                            <Button ref={addButton} id="delivery-reports-add-level" size="small" startIcon={<Add />} onClick={(event) => setAnchor(event.currentTarget)} aria-haspopup="menu" aria-expanded={anchor ? 'true' : 'false'} aria-controls={anchor ? 'delivery-reports-add-level-menu' : undefined} sx={{ ml: -1, textTransform: 'none' }}>
                                Add folder level
                            </Button>
                            <Menu id="delivery-reports-add-level-menu" open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} MenuListProps={{ 'aria-labelledby': 'delivery-reports-add-level' }}>
                                {remaining.map((facet) => (
                                    <MenuItem key={facet} onClick={() => add(facet)}>
                                        {facetLabel(facet, localization)}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}
                    <TreeRow depth={layout.length + 1} icon={<PictureAsPdfOutlined />} label={sample.filename} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={() => onDownload(layout)} variant="contained">
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeliveryReportsFolderDialog;
